import { formatDistanceToNow } from 'date-fns'
import CantileverTheme from 'styles/cantileverTheme'
import { RecentRoute } from 'types'

export interface FormattedRecentRoute {
  id: string
  title: string
  time: string
  color: string
  pathname: string
  search?: Record<string, unknown>
}

/**
 * Format a recent route for display in the UI
 */
export function formatRecentRoute(route: RecentRoute): FormattedRecentRoute {
  const { pathname, search, timestamp, title } = route

  // Generate a human-readable title from pathname if not provided
  const displayTitle = title || generateTitleFromPath(pathname)

  // Generate color based on page title
  const color = generateColorFromTitle(displayTitle)

  // Format timestamp
  const timeAgo = formatDistanceToNow(timestamp)

  return {
    id: `${pathname}-${timestamp}`,
    title: displayTitle,
    time: timeAgo,
    color,
    pathname,
    search,
  }
}

/**
 * Generate a title from a pathname when no page title is found from the saved route
 * @param pathname The pathname to generate a title from
 * @returns A title for the page
 */
function generateTitleFromPath(pathname: string): string {
  // Extract meaningful parts from pathname
  const parts = pathname.split('/').filter(Boolean)

  if (parts.length === 0) return 'Home'

  // Handle specific route patterns
  if (pathname.includes('/assets/')) {
    return 'Asset Details'
  }
  if (pathname.includes('/system-lens/')) {
    return 'System Lens'
  }
  if (pathname.includes('/value-stories/')) {
    return 'Value Story'
  }
  if (pathname.includes('/job/')) {
    return 'Job'
  }
  if (pathname.includes('/home/')) {
    return 'Home'
  }
  if (pathname.includes('/reporting/')) {
    return 'Reporting'
  }
  if (pathname.includes('/process-flows/')) {
    return 'Process Flows'
  }
  if (pathname.includes('/health-plans/')) {
    return 'Health Plans'
  }

  // Fallback: capitalize the first part
  return parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
}

/**
 * Generate a consistent color for a page title
 * @param title The page title
 * @returns A hex color code
 */
function generateColorFromTitle(title: string): string {
  const colors = CantileverTheme.colors

  // Generate consistent colors based on page title using theme colors
  const colorMap: Record<string, string> = {
    // Home & Overview
    Home: colors.green['500_base'],
    Overview: colors.green['600'],
    Landing: colors.slate['500'],

    // Assets
    Assets: colors.gold['600'],
    'Asset Details': colors.gold['600'],
    'Asset Lens': colors.gold['700'],
    'Asset Assessment': colors.gold['400'],

    // System & Analysis
    'System Lens': colors.slate['800'],
    Explorer: colors.blue['500_base'],
    'Drilldown Exploration': colors.gecko_brand['400'],

    // Jobs & Scheduling
    Jobs: colors.green['600'],
    'Job Details': colors.green['500_base'],
    Scheduling: colors.slate['500'],

    // Inspections & Reporting
    Inspections: colors.gold['700'],
    Reporting: colors.slate['500'],
    Report: colors.slate['500'],
    'New Report': colors.gecko_brand['400'],

    // Health & Planning
    'Health Plans': colors.success['600'],
    Walkdowns: colors.green['600'],
    'Walkdown Details': colors.green['500_base'],

    // Value Stories
    'Value Stories': colors.blue['500_base'],
    'Value Story Details': colors.gecko_brand['400'],
    'Add Value Story': colors.blue['400'],
    'Edit Value Story': colors.blue['500_base'],

    // Process & Visualization
    'Process Flows': colors.gecko_brand['400'],
    'Process Flow Diagram': colors.blue['500_base'],
    'Point Clouds': colors.slate['800'],
    'Point Cloud': colors.slate['500'],

    // Posts & Communication
    Posts: colors.gold['600'],
    'Post Details': colors.gold['700'],

    // Viewers
    'Inspection Viewer': colors.slate['800'],
    'Configuration Viewer': colors.blue['500_base'],

    // Inventory Management
    Inventory: colors.gold['600'],
    Parts: colors.gold['400'],
    Equipment: colors.gold['700'],
    Assembly: colors.gold['500_base'],
    'Sub-Assembly': colors.gold['400'],
    'Work Order': colors.warning['400_base'],

    // Organization & Admin
    Organization: colors.slate['500'],
    'Organization Details': colors.slate['600'],
    Site: colors.green['600'],
    'Site Details': colors.green['500_base'],
    'Manage Users': colors.blue['500_base'],
    Extensions: colors.gecko_brand['400'],

    // Integration & Import
    'Import RAM': colors.gold['600'],
    'Maximo Work Orders': colors.warning['400_base'],
    'Object View': colors.blue['500_base'],
  }

  return colorMap[title] || colors.slate['500']
}
